import { useState, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { BannerHeader, Modal } from '@/primer/components';
import { useReservationTimer } from '@/hooks/useReservationTimer';
import { SECONDS_PER_MINUTE } from '@/utils';

interface Props {
  campaignId: number;
  reservedDate: string;
}

/** Countdown banner for an active product reservation. */
export function ReservationTimerBanner({ campaignId, reservedDate }: Props) {
  const [infoOpen, setInfoOpen] = useState(false);
  const navigate = useNavigate();
  const expiryHandledRef = useRef(false);

  const { secondsLeft, hasExpired } = useReservationTimer(reservedDate);

  useEffect(() => {
    expiryHandledRef.current = false;
  }, [reservedDate]);

  useEffect(() => {
    if (!hasExpired || expiryHandledRef.current) return;
    expiryHandledRef.current = true;
    navigate({ to: '/campaign/$id/select-product', params: { id: campaignId }, replace: true });
  }, [hasExpired, campaignId, navigate]);

  if (hasExpired || secondsLeft <= 0) return null;

  const mins = Math.floor(secondsLeft / SECONDS_PER_MINUTE);
  const secs = String(secondsLeft % SECONDS_PER_MINUTE).padStart(2, '0');

  return (
    <>
      <BannerHeader
        state="timer"
        text={`Product reserved for ${mins}:${secs}`}
        onClick={() => setInfoOpen(true)}
      />
      <Modal
        isOpen={infoOpen}
        onClose={() => setInfoOpen(false)}
        icon="circle-info-filled"
        title='What does "reserved" mean?'
        description="The selected product(s) are kept for you until the timer runs out. That way, you have time to review the requirements for this deal before claiming ✅ When the timer runs out, the product(s) will be released and you will need to reselect them again."
        primaryButton={{ label: 'Got it', onClick: () => setInfoOpen(false) }}
      />
    </>
  );
}
